import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { SignatureEdit } from 'webapp/components/common/Signature'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextField,
} from '@common/material'
import { Text } from '@fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import { useRejectTransfer } from '@core/react-query/features/permits/permit'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'
import { Section } from '@common/Section'
import moment from 'moment'
import { useForm } from 'react-hook-form'

type ComponentType = {
  permit: Permit
}

export const RejectPermitTransferModal: React.FC<ComponentType> = ({
  permit,
}) => {
  const rejectTransferPermit = useRejectTransfer()
  const { hideModal } = useModal()
  const { data: profile } = useGetProfile()
  const [error, setError] = useState('')
  const toast = useToast()

  const { message, createdOn, sentTo, createdBy } = permit?.lastTransfer
  const datetimeString = moment(createdOn).format('D MMMM YYYY HH:mm')

  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      permitId: permit.id,
      rejectionReason: '',
    },
  })

  const onSubmit = handleSubmit((data) => {
    rejectTransferPermit.mutate(data, {
      onError: (e) => {
        setError(e.message)
        toast.error(e.message)
      },
      onSuccess: () => {
        toast.success(localizedStrings.transferRejected)
        hideModal()
      },
    })
  })

  return (
    <ModalDialogContainer>
      <form onSubmit={onSubmit}>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20 }}
        >
          {localizedStrings.rejectTransfer}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
        >
          <Section label={localizedStrings.intendedRecipient} mt={0.5}>
            <Text>{sentTo?.getFullName()}</Text>
          </Section>

          <Section
            label={`Note from ${createdBy?.getFullName()} - ${datetimeString}`}
            mt={2}
          >
            <Text>{message}</Text>
          </Section>

          <TextField
            label={localizedStrings.rejectionDetails}
            placeholder={localizedStrings.reasonForRejectionDots}
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            {...register('rejectionReason')}
            sx={{ mt: 4 }}
          />

          <SignatureEdit readonly value={profile?.signatureUrl} />
          <ErrorMessage>{error}</ErrorMessage>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button variant='outlined' onClick={hideModal}>
            {localizedStrings.cancel}
          </Button>
          <Button
            variant='contained'
            type='submit'
            disabled={!isDirty}
            data-test='permit-transfer-reject'
          >
            {localizedStrings.submit}
          </Button>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}
